// Configuracao da vitrine publica (tenants.storefront_settings). Leitura via
// RLS do tenant; escrita via save_storefront_settings (gate PRO + validacao).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { normalizeStorefrontSettings, type StorefrontSettings } from "@lucralink/shared";
import { supabase } from "@/lib/supabase";
import { mapRpcError } from "@/lib/rpc-error";

const KEY = ["storefront-settings"] as const;

const ERRORS: Readonly<Record<string, string>> = {
  storefront_requires_pro: "A vitrine é do plano PRO.",
  storefront_invite_url_invalid: "Link do convite inválido: use um endereço https.",
  storefront_social_invalid: "Link de rede social inválido: use um endereço https.",
  storefront_items_per_page_invalid: "Itens por página deve ficar entre 1 e 100.",
  storefront_text_too_long: "Algum texto passou do limite de caracteres.",
  storefront_setting_invalid: "Opção inválida na configuração da vitrine. Recarregue a página e tente de novo.",
};

export function storefrontSettingsError(error: unknown): string {
  return mapRpcError(error, ERRORS, "Não foi possível salvar a configuração da vitrine.");
}

export function useStorefrontSettings() {
  return useQuery<StorefrontSettings, Error>({
    queryKey: KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants")
        .select("storefront_settings")
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return normalizeStorefrontSettings((data as { storefront_settings?: unknown } | null)?.storefront_settings);
    },
    staleTime: 30_000,
  });
}

/** Payload enviado: so redes preenchidas e convite sem link vira null. */
export function settingsPayload(settings: StorefrontSettings): Record<string, unknown> {
  const socials = Object.fromEntries(Object.entries(settings.socials).filter(([, url]) => typeof url === "string" && url.trim().length > 0));
  return { ...settings, socials, invite: { ...settings.invite, url: settings.invite.url?.trim() || null } };
}

export function useSaveStorefrontSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (settings: StorefrontSettings) => {
      const { error } = await supabase.rpc("save_storefront_settings", { p_settings: settingsPayload(settings) });
      if (error) throw new Error(storefrontSettingsError(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
